import React, { useEffect, useState } from "react";
import { api, SCREEN_LABELS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../components/ConfirmDialog";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, ShieldCheck, Users as UsersIco, Lock, Key, CheckSquare, Square } from "@phosphor-icons/react";

export default function Users() {
  const { user: me } = useAuth();
  const { confirm } = useConfirm();
  const isAdmin = me?.role === "admin";

  const [tab, setTab] = useState("users");   // users | roles
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [screens, setScreens] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [open, setOpen] = useState(null);       // user or "new"
  const [openRole, setOpenRole] = useState(null); // role or "new"
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/users"),
      api.get("/permissions/screens"),
      api.get("/roles"),
    ]);
    setUsers(a.data); setScreens(b.data.screens); setDefaults(b.data.defaults);
    setRoles(c.data);
    setSelected(new Set());
  };
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    const ok = await confirm({
      title: "Delete user?",
      message: `${u.name} (${u.email}) will lose access immediately.`,
      tone: "danger",
      confirmLabel: "Delete User",
    });
    if (!ok) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const unlock = async (u) => {
    const ok = await confirm({
      title: "Unlock account?",
      message: `${u.name} (${u.email}) will be unlocked and their failed-attempt counter reset to zero.`,
      confirmLabel: "Unlock",
    });
    if (!ok) return;
    try {
      await api.post(`/users/${u.id}/unlock`);
      toast.success(`${u.name} unlocked`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Unlock failed"); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selected.size} user${selected.size > 1 ? "s" : ""}?`,
      message: `This will remove ${selected.size} user account(s) and revoke their access. This cannot be undone.`,
      tone: "danger",
      confirmLabel: `Delete ${selected.size}`,
      confirmText: selected.size >= 3 ? "DELETE" : null,
    });
    if (!ok) return;
    let failed = 0;
    for (const id of selected) {
      try { await api.delete(`/users/${id}`); } catch { failed++; }
    }
    if (failed) toast.error(`${failed} user(s) failed to delete`);
    else toast.success(`Deleted ${selected.size} user(s)`);
    load();
  };

  const toggleSel = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => {
    if (selected.size === users.length) setSelected(new Set());
    else setSelected(new Set(users.filter(u => u.id !== me?.id).map(u => u.id)));
  };

  const delRole = async (r) => {
    if (r.is_system) { toast.error("System roles cannot be deleted"); return; }
    const ok = await confirm({
      title: "Delete this role?",
      message: `Role "${r.label || r.name}" will be removed. Users currently assigned to it will lose their permissions.`,
      tone: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6 bg-zinc-50/40 min-h-screen">
      <PageHeader
        label="ACCESS CONTROL"
        title="Users & Roles"
        subtitle="Create users, define custom roles, and assign which screens each role can access."
        action={
          tab === "users"
            ? <Btn onClick={() => setOpen("new")} data-testid="new-user" className="shadow-sm rounded-xl px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-white font-medium text-xs tracking-wide transition-all flex items-center gap-2"><Plus size={14} weight="bold" /> Add User</Btn>
            : <Btn onClick={() => setOpenRole("new")} data-testid="new-role" className="shadow-sm rounded-xl px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-white font-medium text-xs tracking-wide transition-all flex items-center gap-2"><Plus size={14} weight="bold" /> Add Role</Btn>
        }
      />

      {/* Tabs Layout */}
      <div className="flex flex-wrap gap-1 border-b border-zinc-200/80">
        <button onClick={() => setTab("users")} data-testid="tab-users"
          className={`px-5 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-px transition-all flex items-center gap-2.5 ${
            tab === "users" ? "text-zinc-900 border-zinc-900 font-bold" : "text-zinc-400 border-transparent hover:text-zinc-600"
          }`}
        >
          <UsersIco size={15} weight={tab === "users" ? "fill" : "regular"} />
          <span>Users</span>
          <span className={`font-mono text-[11px] px-1.5 py-0.5 rounded-full ${tab === "users" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500"}`}>{users.length}</span>
        </button>
        <button onClick={() => setTab("roles")} data-testid="tab-roles"
          className={`px-5 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-px transition-all flex items-center gap-2.5 ${
            tab === "roles" ? "text-zinc-900 border-zinc-900 font-bold" : "text-zinc-400 border-transparent hover:text-zinc-600"
          }`}
        >
          <Lock size={15} weight={tab === "roles" ? "fill" : "regular"} />
          <span>Roles</span>
          <span className={`font-mono text-[11px] px-1.5 py-0.5 rounded-full ${tab === "roles" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500"}`}>{roles.length}</span>
        </button>
      </div>

      {/* Bulk Action Strip */}
      {tab === "users" && isAdmin && selected.size > 0 && (
        <div className="bg-zinc-900 text-white rounded-2xl p-4 flex items-center justify-between shadow-lg animate-in fade-in slide-in-from-top-2 duration-200 border border-zinc-800">
          <div className="text-sm font-medium tracking-wide flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[11px] font-bold text-white font-mono">{selected.size}</span>
            <span>accounts marked for removal</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setSelected(new Set())} className="text-xs font-bold uppercase tracking-wider text-zinc-400 hover:text-white transition-colors px-2 py-1">Cancel</button>
            <button
              onClick={bulkDelete}
              data-testid="bulk-delete-users"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-md shadow-red-900/20 transition-all active:scale-95"
            >
              <Trash size={13} weight="bold" /> DELETE Selected
            </button>
          </div>
        </div>
      )}

      {/* Users View Container */}
      {tab === "users" && (
        <Card className="!p-0 border border-zinc-200/60 bg-white rounded-2xl overflow-hidden shadow-sm">
          {users.length === 0 ? (
            <div className="p-12"><EmptyState title="No users found" description="Get started by adding your first user account." /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50/70 text-zinc-400 text-[11px] font-bold uppercase tracking-wider">
                    {isAdmin && (
                      <th className="p-4 w-12 text-center">
                        <input type="checkbox" checked={selected.size > 0 && selected.size === users.filter(u => u.id !== me?.id).length} onChange={toggleAll} data-testid="sel-all-users" className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 w-4 h-4 cursor-pointer" />
                      </th>
                    )}
                    <th className="p-4">Name</th>
                    <th className="p-4">Email Address</th>
                    <th className="p-4">Primary Role</th>
                    <th className="p-4">Screen Permissions</th>
                    <th className="p-4">Security Status</th>
                    <th className="p-4 w-28 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-sm text-zinc-700">
                  {users.map(u => (
                    <tr key={u.id} data-testid={`user-row-${u.email}`} className={`hover:bg-zinc-50/60 transition-colors group ${selected.has(u.id) ? "bg-red-50/40 hover:bg-red-50/60" : ""}`}>
                      {isAdmin && (
                        <td className="p-4 text-center">
                          {u.id !== me?.id ? (
                            <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSel(u.id)} data-testid={`sel-${u.email}`} className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 w-4 h-4 cursor-pointer" />
                          ) : <span className="text-[10px] text-zinc-400 font-mono font-bold bg-zinc-100 px-1.5 py-0.5 rounded uppercase tracking-wide">Self</span>}
                        </td>
                      )}
                      <td className="p-4 font-semibold text-zinc-900">{u.name}</td>
                      <td className="p-4 font-mono text-xs text-zinc-500">{u.email}</td>
                      <td className="p-4">
                        <Badge tone={u.role === "admin" ? "orange" : "zinc"} className="capitalize font-medium tracking-wide">
                          {u.role}
                        </Badge>
                      </td>
                      <td className="p-4 text-xs text-zinc-500 max-w-xs xl:max-w-md truncate" title={(u.permissions || []).map(s => SCREEN_LABELS[s] || s).join(", ")}>
                        {u.role === "admin" ? (
                          <span className="font-semibold text-zinc-800 tracking-wide text-[11px] bg-zinc-100 px-2 py-0.5 rounded">ALL ACCESS</span>
                        ) : (u.permissions || []).map(s => SCREEN_LABELS[s] || s).join(", ") || <span className="italic text-zinc-400">Restricted</span>}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          {u.locked ? (
                            <Badge tone="red" data-testid={`status-${u.email}`} className="w-fit">
                              <Lock size={10} weight="fill" className="inline mr-1" />locked
                            </Badge>
                          ) : (
                            <Badge tone={u.active ? "green" : "red"} data-testid={`status-${u.email}`} className="w-fit">
                              <span className={`h-1.5 w-1.5 rounded-full mr-1.5 inline-block ${u.active ? "bg-emerald-500" : "bg-rose-500"}`} />
                              {u.active ? "active" : "disabled"}
                            </Badge>
                          )}
                          {(u.failed_login_attempts || 0) > 0 && !u.locked && (
                            <span className="text-[10px] font-mono font-medium text-amber-600 mt-0.5 flex items-center gap-1">
                              <Key size={10} /> {u.failed_login_attempts}/3 bad attempts
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex gap-2 justify-end opacity-80 group-hover:opacity-100 transition-opacity">
                          {u.locked && (
                            <button onClick={() => unlock(u)} className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all" data-testid={`unlock-${u.email}`} title="Unlock account">
                              <Lock size={14} weight="bold" />
                            </button>
                          )}
                          <button onClick={() => setOpen(u)} className="p-1.5 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 rounded-lg transition-all" data-testid={`edit-${u.email}`} title="Edit Settings"><PencilSimple size={14} weight="bold" /></button>
                          <button onClick={() => del(u)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Remove Account"><Trash size={14} weight="bold" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Roles View Container */}
      {tab === "roles" && (
        <Card className="!p-0 border border-zinc-200/60 bg-white rounded-2xl overflow-hidden shadow-sm">
          {roles.length === 0 ? (
            <div className="p-12"><EmptyState title="No roles declared" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50/70 text-zinc-400 text-[11px] font-bold uppercase tracking-wider">
                    <th className="p-4">Role Hierarchy</th>
                    <th className="p-4 w-28">Type</th>
                    <th className="p-4">Matrix Map Permissions</th>
                    <th className="p-4 w-32">Active Users</th>
                    <th className="p-4 w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-sm text-zinc-700">
                  {roles.map(r => {
                    const userCount = users.filter(u => u.role === r.name).length;
                    return (
                      <tr key={r.id} data-testid={`role-row-${r.name}`} className="hover:bg-zinc-50/60 transition-colors group">
                        <td className="p-4">
                          <div className="font-semibold text-zinc-900">{r.label || r.name}</div>
                          <div className="font-mono text-[10px] text-zinc-400 mt-0.5">{r.name}</div>
                        </td>
                        <td className="p-4">
                          {r.is_system
                            ? <Badge tone="orange" className="tracking-wide">system</Badge>
                            : <Badge tone="zinc" className="tracking-wide">custom</Badge>}
                        </td>
                        <td className="p-4 text-xs text-zinc-500 max-w-sm xl:max-w-xl">
                          {r.name === "admin" ? (
                            <span className="font-bold text-zinc-800 tracking-wider text-[11px] bg-zinc-100 px-2 py-0.5 rounded">SUPER ADMINISTRATOR MATRIX</span>
                          ) : (r.permissions || []).map(s => SCREEN_LABELS[s] || s).join(" · ") || <span className="italic text-zinc-300">Null screen vector mapping</span>}
                        </td>
                        <td className="p-4 font-mono font-medium text-zinc-600 pl-6">{userCount}</td>
                        <td className="p-4 text-right">
                          <div className="flex gap-2 justify-end opacity-80 group-hover:opacity-100 transition-opacity">
                            {r.name !== "admin" && (
                              <button onClick={() => setOpenRole(r)} className="p-1.5 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 rounded-lg transition-all" data-testid={`edit-role-${r.name}`} title="Modify Parameters">
                                <PencilSimple size={14} weight="bold" />
                              </button>
                            )}
                            {!r.is_system && (
                              <button onClick={() => delRole(r)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Destroy Role Profile">
                                <Trash size={14} weight="bold" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Modals Core Mount Hooks */}
      {open && (
        <UserModal
          user={open === "new" ? null : open}
          screens={screens}
          defaults={defaults}
          roles={roles}
          onClose={() => setOpen(null)}
          onSaved={() => { setOpen(null); load(); }}
        />
      )}
      {openRole && (
        <RoleModal
          role={openRole === "new" ? null : openRole}
          screens={screens}
          onClose={() => setOpenRole(null)}
          onSaved={() => { setOpenRole(null); load(); }}
        />
      )}
    </div>
  );
}

function RoleModal({ role, screens, onClose, onSaved }) {
  const isNew = !role;
  const isAdminRole = role?.name === "admin";
  const [actions, setActions] = React.useState(["read", "create", "update", "delete"]);

  React.useEffect(() => {
    api.get("/permissions/screens").then(r => {
      if (Array.isArray(r.data.actions) && r.data.actions.length) setActions(r.data.actions);
    }).catch(() => {});
  }, []);

  const [f, setF] = useState({
    name: role?.name || "",
    label: role?.label || "",
    permissions: role?.permissions || [],
    actions: role?.actions || {},
  });
  const isSystem = role?.is_system;

  const togglePerm = (s) => setF(prev => {
    const has = prev.permissions.includes(s);
    const newPerms = has ? prev.permissions.filter(x => x !== s) : [...prev.permissions, s];
    const newActs = { ...prev.actions };
    if (has) delete newActs[s];
    else newActs[s] = [...actions];
    return { ...prev, permissions: newPerms, actions: newActs };
  });

  const toggleAllScreens = () => setF(prev => {
    const allOn = prev.permissions.length === screens.length;
    return allOn
      ? { ...prev, permissions: [], actions: {} }
      : { ...prev, permissions: [...screens], actions: Object.fromEntries(screens.map(s => [s, [...actions]])) };
  });

  const toggleAction = (screen, action) => setF(prev => {
    const cur = new Set(prev.actions?.[screen] || []);
    if (cur.has(action)) cur.delete(action);
    else cur.add(action);
    if (cur.has("create") || cur.has("update") || cur.has("delete")) cur.add("read");
    return { ...prev, actions: { ...prev.actions, [screen]: Array.from(cur) } };
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isNew) {
        const res = await api.post("/roles", f);
        toast.success(`Role "${f.label || f.name}" created`);
        if (res.data?._dropped_verbs?.length) {
          toast.warning(`Ignored unknown verbs: ${res.data._dropped_verbs.join(", ")}`);
        }
      } else {
        const body = isSystem
          ? { label: f.label, permissions: f.permissions, actions: f.actions }
          : { name: f.name, label: f.label, permissions: f.permissions, actions: f.actions };
        const res = await api.put(`/roles/${role.id}`, body);
        toast.success("Role updated");
        if (res.data?._dropped_verbs?.length) {
          toast.warning(`Ignored unknown verbs: ${res.data._dropped_verbs.join(", ")}`);
        }
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "New Role Blueprint" : `Modify Role Profile · ${role.label || role.name}`} size="lg">
      <form onSubmit={submit} className="space-y-6" data-testid="role-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Role Identifier string (Lowercase tokens, system variables)">
            <Input
              value={f.name}
              onChange={e => setF({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })}
              required
              disabled={!isNew || isSystem}
              placeholder="e.g. warehouse_manager"
              data-testid="role-name"
              className="rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900 shadow-sm"
            />
          </Field>
          <Field label="Client Display Label">
            <Input
              value={f.label}
              onChange={e => setF({ ...f, label: e.target.value })}
              placeholder="e.g. Warehouse Manager"
              data-testid="role-label"
              className="rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900 shadow-sm"
            />
          </Field>
        </div>

        {isSystem && (
          <div className="bg-amber-50 text-amber-800 border border-amber-200/60 rounded-xl p-3.5 text-xs font-medium flex items-start gap-2">
            <ShieldCheck size={16} className="mt-0.5 text-amber-600 flex-shrink-0" />
            <span><b>System Core Vector Protected:</b> The underlying token identification map cannot be mutated, but display structures and permissions remain configurable.</span>
          </div>
        )}

        {isAdminRole && (
          <div className="bg-zinc-900 text-zinc-100 border border-zinc-800 rounded-xl p-3.5 text-xs font-medium flex items-start gap-2">
            <ShieldCheck size={16} className="mt-0.5 text-emerald-400 flex-shrink-0" />
            <span><b>Global Administrator:</b> Root permission matrix overrides active check values. This system mapping retains all current and newly added functional routing tables.</span>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><ShieldCheck size={15} weight="bold" /> Access Permutations & CRUD Operations</div>
            <button type="button" onClick={toggleAllScreens} className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors">
              {f.permissions.length === screens.length ? "Invert Array Selection" : "Select Global Array"}
            </button>
          </div>
          
          <div className="overflow-x-auto border border-zinc-200/80 rounded-2xl max-h-[400px] overflow-y-auto shadow-sm bg-white" data-testid="role-permission-matrix">
            <table className="w-full text-xs text-left border-collapse">
              <thead className="bg-zinc-50 sticky top-0 border-b border-zinc-200/60 z-10">
                <tr>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wider text-[10px] text-zinc-400">Target Screen Routing Vector</th>
                  {actions.map(a => (
                    <th key={a} className="text-center px-3 py-3 font-mono uppercase tracking-wider text-[10px] text-zinc-400 w-20">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {screens.map(s => {
                  const enabled = f.permissions.includes(s);
                  const verbs = f.actions?.[s] || [];
                  return (
                    <tr key={s} className={`transition-colors ${enabled ? "bg-white" : "bg-zinc-50/40"}`}>
                      <td className="px-4 py-2.5">
                        <label className="flex items-center gap-3 cursor-pointer group py-0.5">
                          <input type="checkbox" checked={enabled} onChange={() => togglePerm(s)} data-testid={`role-perm-${s}`} className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 w-4 h-4" />
                          <span className={`transition-colors text-sm ${enabled ? "font-semibold text-zinc-900" : "text-zinc-400 group-hover:text-zinc-600"}`}>{SCREEN_LABELS[s] || s}</span>
                        </label>
                      </td>
                      {actions.map(a => {
                        const on = enabled && (isAdminRole || verbs.includes(a));
                        const disabled = !enabled || isAdminRole || (a === "read" && (verbs.includes("create") || verbs.includes("update") || verbs.includes("delete")));
                        return (
                          <td key={a} className="px-3 py-2.5 text-center">
                            <input
                              type="checkbox"
                              checked={on}
                              disabled={disabled}
                              onChange={() => toggleAction(s, a)}
                              className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 w-4 h-4 disabled:opacity-30 disabled:cursor-not-allowed mx-auto block"
                              data-testid={`role-action-${s}-${a}`}
                              title={a === "read" && verbs.length > 1 ? "Read flag validation state lock active" : ""}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-zinc-400 mt-2 font-mono text-right tracking-tight px-1">
            Active mapping indicators: {f.permissions.length} nodes tracking · {Object.values(f.actions || {}).reduce((s, v) => s + (v?.length || 0), 0)} operational verbs bound
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-zinc-100">
          <Btn type="button" variant="secondary" onClick={onClose} className="rounded-xl border border-zinc-200 px-4 py-2 text-zinc-600 hover:bg-zinc-50 text-xs font-semibold uppercase tracking-wider transition-colors">Cancel</Btn>
          <Btn type="submit" data-testid="role-submit" className="rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-2 text-xs font-semibold uppercase tracking-wider shadow-md transition-all active:scale-95">{isNew ? "Create Vector" : "Commit Changes"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function UserModal({ user, screens, defaults, roles, onClose, onSaved }) {
  const isNew = !user;
  const initialRole = user?.role || (roles?.[0]?.name || "production");
  const [f, setF] = useState({
    name: user?.name || "",
    email: user?.email || "",
    role: initialRole,
    password: "",
    permissions: user?.permissions || defaults[initialRole] || [],
  });

  const togglePerm = (s) => {
    setF(prev => ({
      ...prev,
      permissions: prev.permissions.includes(s)
        ? prev.permissions.filter(x => x !== s)
        : [...prev.permissions, s],
    }));
  };

  const changeRole = (r) => {
    const roleDef = (roles || []).find(x => x.name === r);
    const perms = roleDef ? (roleDef.permissions || []) : (defaults[r] || []);
    setF({ ...f, role: r, permissions: perms });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isNew) {
        await api.post("/users", f);
        toast.success("User created");
      } else {
        const body = { name: f.name, role: f.role, permissions: f.permissions };
        if (f.password) body.password = f.password;
        await api.put(`/users/${user.id}`, body);
        toast.success("User updated");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Provision User Account" : `Account Operations Profile · ${user.name}`} size="lg">
      <form onSubmit={submit} className="space-y-6" data-testid="user-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name Matrix Reference"><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} required data-testid="user-name" className="rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900" /></Field>
          <Field label="Secure Routing Email"><Input type="email" value={f.email} disabled={!isNew} onChange={e => setF({ ...f, email: e.target.value })} required data-testid="user-email" className="rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900" /></Field>
          <Field label="Assigned Structural Profile Hierarchy">
            <Select value={f.role} onChange={e => changeRole(e.target.value)} data-testid="user-role" className="rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900 w-full bg-white">
              {(roles || []).map(r => (
                <option key={r.name} value={r.name}>
                  {r.label || r.name}{r.is_system ? " [System Core]" : " [Custom Profile]"}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={isNew ? "Secure Password Initialization" : "Password Reset"}>
            <Input type="password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })} required={isNew} data-testid="user-password" className="rounded-xl border-zinc-200 focus:border-zinc-900 focus:ring-zinc-900" />
          </Field>
        </div>

        <div className="space-y-3">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><ShieldCheck size={15} weight="bold" /> Functional Screen Permutation Targets <span className="text-[10px] text-zinc-400 font-mono tracking-normal normal-case ml-2">(Overrides basic role default inheritance matrix)</span></div>
          {f.role === "admin" ? (
            <div className="bg-zinc-900 text-zinc-100 border border-zinc-800 rounded-xl p-4 font-mono text-xs tracking-wide shadow-sm flex items-center gap-2">
              <ShieldCheck size={16} className="text-emerald-400" />
              <span>System administrative accounts override separate granular restriction maps. Full capabilities enabled.</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {screens.map(s => {
                const checked = f.permissions.includes(s);
                return (
                  <label key={s} className={`flex items-center gap-3 text-sm border p-3 cursor-pointer rounded-xl transition-all select-none hover:bg-zinc-50 ${checked ? "border-zinc-900 bg-zinc-50/20 font-semibold text-zinc-900" : "border-zinc-200/80 text-zinc-500"}`}>
                    <input type="checkbox" checked={checked} onChange={() => togglePerm(s)} data-testid={`perm-${s}`} className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 w-4 h-4" />
                    <span>{SCREEN_LABELS[s] || s}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-zinc-100">
          <Btn type="button" variant="secondary" onClick={onClose} className="rounded-xl border border-zinc-200 px-4 py-2 text-zinc-600 hover:bg-zinc-50 text-xs font-semibold uppercase tracking-wider transition-colors">Cancel</Btn>
          <Btn type="submit" data-testid="user-submit" className="rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white px-5 py-2 text-xs font-semibold uppercase tracking-wider shadow-md transition-all active:scale-95">{isNew ? "Provision Account" : "Commit Matrix"}</Btn>
        </div>
      </form>
    </Modal>
  );
}